import React, { useEffect, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import API from '../services/api';
import { formatDay } from '../utils/dates';

const dateStr = (d) => formatDay(d, '');
const STATUS_WORD = { pending: 'nuevo', confirmed: 'confirmado', en_planta: 'en planta', delivered: 'entregado' };

function describe(p) {
  const docs = (p.documents || []).map(d => `${d.type === 'conduce' ? 'Conduce' : 'Factura'} ${d.number}`).join(', ');
  const units = (p.items || []).reduce((sum, it) => sum + (it.quantity || 0), 0);
  const who = p.origin === 'auto' ? 'venta' : p.origin === 'whatsapp' ? 'WhatsApp' : 'CG Connect';
  return `${p.number} · ${dateStr(p.createdAt)} · ${units} u. · ${who}, ${STATUS_WORD[p.status] || p.status}${docs ? ` · ${docs}` : ''}`;
}

// "Pedido" for a new conduce or factura: which of the client's pedidos it carries. Left on "Nuevo pedido", one is
// created for it (every sale has a pedido — see backend services/pedidoDocuments.js). For a factura, a pedido whose
// conduce hasn't been invoiced yet is suggested and, if it's the only one, picked — the "deliver on a conduce,
// invoice it later" case — so the client sees one order, not two.
const PedidoPicker = ({ clientId, forType, value, onChange, invoiceId, disabled }) => {
  const [candidates, setCandidates] = useState([]);
  const touched = useRef(false); // once the user chooses, never auto-pick over them

  useEffect(() => {
    setCandidates([]);
    if (!clientId) return;
    let alive = true;
    API.get('/order-requests/candidates', { params: { client: clientId, for: forType, invoice: invoiceId } })
      .then(({ data }) => {
        if (!alive) return;
        setCandidates(data);
        const suggested = data.filter(p => p.suggested);
        if (!touched.current && !value && suggested.length === 1) onChange(suggested[0]._id);
      })
      .catch(() => alive && setCandidates([]));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, forType, invoiceId]);

  // The current value may not be a candidate any more (e.g. an invoice being edited): keep it selectable.
  const hasValue = !value || candidates.some(p => p._id === value);
  const chosen = candidates.find(p => p._id === value);

  return (
    <div>
      <Form.Label>Pedido <span className="text-muted">(lo que el cliente ve en CG Connect)</span></Form.Label>
      <Form.Select
        value={value || ''}
        disabled={disabled || !clientId}
        onChange={e => { touched.current = true; onChange(e.target.value || null); }}
      >
        <option value="">Nuevo pedido (se crea automáticamente)</option>
        {!hasValue && <option value={value}>Pedido ya elegido</option>}
        {candidates.map(p => (
          <option key={p._id} value={p._id} disabled={!!p.takenBy}>
            {p.suggested ? '★ ' : ''}{describe(p)}{p.takenBy ? ` — ya en la factura ${p.takenBy}` : ''}
          </option>
        ))}
      </Form.Select>
      {chosen?.suggested && (
        <div className="small mt-1" style={{ color: 'var(--mm-cyan)' }}>
          Se factura el pedido de {chosen.documents.filter(d => d.type === 'conduce').map(d => `conduce ${d.number}`).join(', ')}: el cliente seguirá viendo un solo pedido.
        </div>
      )}
      {!value && candidates.some(p => p.suggested) && (
        <div className="small mt-1 text-warning">
          Este cliente tiene conduces sin facturar. Si esta factura es por uno de ellos, elige su pedido para no duplicarlo.
        </div>
      )}
    </div>
  );
};

export default PedidoPicker;
